import { useEffect, useState } from "react";
import { ArrowLeft, Calendar, Check, Clock, Mail, Users } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { supabase } from "../../../../lib/supabase";
import { createMeeting } from "../../data/appointments";

interface Mentor {
  id: string;
  name: string;
  email: string;
}

interface AvailableTime {
  id: string;
  start_time: string;
  end_time: string;
  date: string;
}

interface RawMentorData {
  uuid: string;
  vincer_base_user: {
    name: string;
    email: string;
  };
}

interface AvailabilitySlot {
  start: string;
  end: string;
}

interface MentorAvailabilityData {
  uuid: string;
  mentor_uuid: string;
  availability_data: AvailabilitySlot[];
  updated_at: string;
}

export function MentorDetailPage() {
  const { mentorId } = useParams<{ mentorId: string }>();
  const navigate = useNavigate();
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const bookingSuccess = false;

  useEffect(() => {
    async function fetchMentorDetails() {
      if (!mentorId) {
        setError("Mentor ID not provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get current user
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setError("User not found");
          return;
        }

        // Fetch mentee record
        const { data: menteeData, error: menteeError } = await supabase
          .from("mentee")
          .select("uuid")
          .eq("user_uuid", user.id)
          .single();

        if (menteeError || !menteeData) {
          setError("Mentee profile not found");
          return;
        }

        // Verify relationship exists
        const { data: relationship, error: relationshipError } = await supabase
          .from("relationship_assignments")
          .select("*")
          .eq("mentee_uuid", menteeData.uuid)
          .eq("mentor_uuid", mentorId)
          .single();

        if (relationshipError || !relationship) {
          setError("You do not have a relationship with this mentor");
          return;
        }

        // Fetch mentor details
        const { data: mentorData, error: mentorError } = await supabase
          .from("mentor")
          .select(
            `
            uuid,
            vincer_base_user (
              name,
              email
            )
          `
          )
          .eq("uuid", mentorId)
          .single();

        if (mentorError || !mentorData) {
          setError("Mentor not found");
          return;
        }

        const typedMentorData = mentorData as unknown as RawMentorData;

        setMentor({
          id: typedMentorData.uuid,
          name: typedMentorData.vincer_base_user.name,
          email: typedMentorData.vincer_base_user.email,
        });

        // Fetch available times from the mentor_availability table
        const { data: availabilityData, error: availabilityError } =
          await supabase
            .from("mentor_availability")
            .select("*")
            .eq("mentor_uuid", mentorId)
            .single();

        if (availabilityError) {
          console.error("Error fetching availability:", availabilityError);
          setError("Failed to fetch available times");
          return;
        }

        if (!availabilityData) {
          setAvailableTimes([]);
          return;
        }

        const typedAvailability = availabilityData as MentorAvailabilityData;
        const now = new Date();

        // Transform the availability data into our AvailableTime format
        const transformedTimes: AvailableTime[] =
          typedAvailability.availability_data
            .filter((slot) => {
              const startTime = new Date(slot.start);
              return startTime > now;
            })
            .map((slot, index) => {
              const startTime = new Date(slot.start);
              const endTime = new Date(slot.end);

              return {
                id: `${slot.start}-${index}`,
                start_time: startTime.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                end_time: endTime.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                date: slot.start,
              };
            });

        // Sort by date and time
        transformedTimes.sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.start_time}`);
          const dateB = new Date(`${b.date}T${b.start_time}`);
          return dateA.getTime() - dateB.getTime();
        });

        setAvailableTimes(transformedTimes);
      } catch (err) {
        console.error("Error fetching mentor details:", err);
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchMentorDetails();
  }, [mentorId]);

  const handleTimeSelect = (timeId: string) => {
    setSelectedTime(timeId);
  };

  const handleBooking = async () => {
    if (!selectedTime || !mentor) {
      console.error("Booking failed: Missing required data", {
        selectedTime,
        mentor,
      });
      return;
    }

    try {
      console.log("Starting booking process...", {
        selectedTime,
        mentorId: mentor.id,
      });
      setLoading(true);
      setError(null);

      const selectedTimeSlot = availableTimes.find(
        (time) => time.id === selectedTime
      );
      if (!selectedTimeSlot) {
        console.error("Selected time slot not found in available times", {
          selectedTime,
          availableTimes,
        });
        setError("Selected time slot not found");
        return;
      }

      console.log("Creating meeting with time slot:", selectedTimeSlot);

      // Parse the date and time components
      const [datePart] = selectedTimeSlot.date.split("T");
      const [hours, minutes] = selectedTimeSlot.start_time.split(":");
      const ampm = selectedTimeSlot.start_time.includes("PM") ? "PM" : "AM";

      // Create a Date object in the local timezone
      const localDate = new Date(datePart);
      let hour = parseInt(hours);

      // Convert to 24-hour format
      if (ampm === "PM" && hour !== 12) {
        hour += 12;
      } else if (ampm === "AM" && hour === 12) {
        hour = 0;
      }

      localDate.setHours(hour, parseInt(minutes), 0, 0);

      // Convert to ISO string (UTC)
      const scheduledTime = localDate.toISOString();
      console.log("Formatted scheduled time (UTC):", scheduledTime);

      const meetingResult = await createMeeting(mentor.id, scheduledTime);
      console.log("Meeting creation result:", meetingResult);

      // Navigate to appointments page
      console.log("Booking successful, navigating to appointments page");
      navigate("/mentee/appointments");
    } catch (err) {
      console.error("Error booking meeting:", err);
      if (err instanceof Error) {
        console.error("Error details:", {
          message: err.message,
          stack: err.stack,
          name: err.name,
        });
      }
      setError("Failed to book meeting");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl">
        <button
          onClick={() => navigate("/mentee/mentors")}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="size-4" />
          <span>Back to Mentors</span>
        </button>
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600">Loading mentor details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl">
        <button
          onClick={() => navigate("/mentee/mentors")}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="size-4" />
          <span>Back to Mentors</span>
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          <p className="font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="max-w-4xl">
        <button
          onClick={() => navigate("/mentee/mentors")}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="size-4" />
          <span>Back to Mentors</span>
        </button>
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-green-100 p-3">
              <Check className="size-8 text-green-600" />
            </div>
          </div>
          <h2 className="mb-2 text-2xl font-bold">Booking Successful!</h2>
          <p className="mb-6 text-gray-600">
            Your meeting has been scheduled. You will receive a confirmation
            email shortly.
          </p>
          <button
            onClick={() => navigate("/mentee/appointments")}
            className="rounded-lg bg-[#ffcc32] px-6 py-2 text-black transition-colors hover:bg-[#ffd65c]"
          >
            View My Appointments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <button
        onClick={() => navigate("/mentee/mentors")}
        className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="size-4" />
        <span>Back to Mentors</span>
      </button>

      {mentor && (
        <div className="mb-8 rounded-xl border bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-4">
            <div className="rounded-lg bg-vincer-50 p-3">
              <Users className="size-6 text-vincer-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{mentor.name}</h1>
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="size-4" />
                <span>{mentor.email}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-bold">Available Times</h2>

        {availableTimes.length === 0 ? (
          <p className="text-gray-600">
            No available times found for this mentor.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {availableTimes.map((time) => (
              <div
                key={time.id}
                className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                  selectedTime === time.id
                    ? "border-vincer-500 bg-vincer-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => handleTimeSelect(time.id)}
              >
                <div className="mb-2 flex items-center gap-2">
                  <Calendar className="size-4 text-gray-500" />
                  <span className="font-medium">
                    {new Date(time.date).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-gray-500" />
                  <span>
                    {time.start_time} - {time.end_time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedTime && (
          <div className="mt-6">
            <button
              onClick={handleBooking}
              disabled={loading}
              className="rounded-lg bg-vincer-500 px-6 py-2 text-white transition-colors hover:bg-vincer-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Booking..." : "Book Session"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
